import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { serializeError } from 'serialize-error';

import typeCheck from 'common/util/typeCheck';

import {
  SR_API_ACTION_CODEBOX_GET_LIST,
  SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
  SR_API_ACTION_CODEBOX_UNASSIGN_LISTING_ID,
} from 'api/actionTypes';

import { SCREEN_ID_CODEBOX_DASHBOARD } from 'client/constants/screenIds';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import allowedRoles from 'client/helpers/allowedRoles';
import withScrollToTop from 'client/helpers/withScrollToTop';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import CodeboxActions from 'client/actions/Codebox';
import postWithState from 'client/actions/postWithState';

import Navbar from 'client/components/Navbar';
import SrCodeboxRegisterDeviceForm from 'client/components/SrCodeboxRegisterDeviceForm';
import SrTable from 'client/components/SrTable';

@withScrollToTop
@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  getCodeboxListApiState: {
    action: SR_API_ACTION_CODEBOX_GET_LIST,
  },
})
@withScreenId(SCREEN_ID_CODEBOX_DASHBOARD)
@branch({
  codeboxList: ['codebox', 'list'],
  currentUser: ['currentUser'],
})
class CodeboxDashboardPage extends Component {
  static fetchData(state, inject) {
    return CodeboxActions.getCodeboxList(inject);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Codebox | Sunroom Rentals',
  });

  static propTypes = {
    codeboxList: PropTypes.array,
    getCodeboxListApiState: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isBusy: false,
      apiResponse: null,
      apiError: null,
    };
    this._dispatchCodeboxAction = this._dispatchCodeboxAction.bind(this);
  }

  async _dispatchCodeboxAction({ action, payload }) {
    const { dispatch } = this.props;

    try {
      typeCheck('action::NonEmptyString', action);
      typeCheck('payload::Maybe Object', payload);

      this.setState({
        isBusy: true,
        apiResponse: null,
        apiError: null,
      });

      const apiResponse = await dispatch(postWithState, { action, payload });

      await dispatch(CodeboxActions.getCodeboxList);

      this.setState({
        isBusy: false,
        apiResponse: apiResponse,
        apiError: null,
      });
    } catch (error) {
      log.error(error);
      this.setState({
        isBusy: false,
        apiResponse: null,
        apiError: error,
      });
    }
  }

  _onUnassignListingIdClick = (event) => {
    const { currentUser } = this.props;
    if (!global.confirm("Are you sure you want to unassign this Codebox's listing?")) {
      return;
    }
    const codeboxLockId = parseInt(event.target.dataset.codeboxLockId, 10);
    this._dispatchCodeboxAction({
      action: SR_API_ACTION_CODEBOX_UNASSIGN_LISTING_ID,
      payload: {
        triggeredBy: currentUser.name,
        codeboxLockId,
      },
    });
  };

  _onAssignListingIdClick = (event) => {
    const { currentUser } = this.props;
    const listingIdString = global.prompt(
      'Please enter the listing ID you want to assign to this codebox.',
    );
    if (!listingIdString) {
      return;
    }
    const listingId = parseInt(listingIdString, 10);
    if (!listingId || isNaN(listingId)) {
      global.alert(`"${listingIdString}" is not a valid listing ID.`);
      return;
    }
    const codeboxLockId = parseInt(event.target.dataset.codeboxLockId, 10);
    this._dispatchCodeboxAction({
      action: SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
      payload: {
        triggeredBy: currentUser.name,
        codeboxLockId,
        listingId,
      },
    });
  };

  render() {
    const { codeboxList, getCodeboxListApiState } = this.props;

    const { isBusy, apiResponse, apiError } = this.state;

    return (
      <div className="page-admin">
        <div className="adminContainer">
          <Navbar redirectLogoTo="/admin" />

          <div className="section">
            {isBusy || getCodeboxListApiState.isBusy || !codeboxList ? (
              <div className="loading-gif" />
            ) : (
              <div>
                {apiResponse && (
                  <div>
                    Success:
                    <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
                  </div>
                )}
                {apiError && (
                  <div>
                    Error:
                    <pre>{JSON.stringify(serializeError(apiError), null, 2)}</pre>
                  </div>
                )}

                <SrTable
                  headers={[
                    { content: 'ID' },
                    { content: 'Created' },
                    { content: 'Serial Number' },
                    { content: 'Admin Code' },
                    { content: 'Master Code' },
                    { content: 'Shackle Code' },
                    { content: 'Listing' },
                    { content: 'Actions' },
                  ]}
                  rows={codeboxList.map((codeboxLock) => {
                    return {
                      cells: [
                        { content: codeboxLock.id },
                        { content: moment(codeboxLock.createdDate).format('ll') },
                        { content: codeboxLock.serialNumber },
                        { content: codeboxLock.adminCode },
                        { content: codeboxLock.masterCode },
                        { content: codeboxLock.shackleCode },
                        {
                          content: codeboxLock.listingId
                            ? `${codeboxLock.listingId} - ${codeboxLock.listingAddressStreetDisplay}`
                            : '--',
                        },
                        {
                          content: (
                            <div>
                              {codeboxLock.listingId ? (
                                <div
                                  data-codebox-lock-id={codeboxLock.id}
                                  onClick={this._onUnassignListingIdClick}
                                >
                                  Unassign Listing
                                </div>
                              ) : (
                                <div
                                  data-codebox-lock-id={codeboxLock.id}
                                  onClick={this._onAssignListingIdClick}
                                >
                                  Assign Listing
                                </div>
                              )}
                            </div>
                          ),
                        },
                      ],
                    };
                  })}
                />
                <SrCodeboxRegisterDeviceForm />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

CodeboxDashboardPage.displayName = 'CodeboxDashboardPage';

export default CodeboxDashboardPage;
